import moment from 'moment';

import type {AdminSchool} from './useAdminSchools';

/**
 * When the school's restrictions are scheduled for removal — `AdminSchool.restrictionRemovalDate`,
 * the end date of the school's running `contentLockGracePeriods` row (#20975), stamped by
 * `useAdminSchools`. Absent (null here) when the school isn't in a grace window, which hides
 * every grace-window surface (header note, breakdown tooltip, row warning + locked toggle).
 */
export function getRestrictionRemovalDate(school: AdminSchool): moment.Moment | null {
  // The backend row is the authority — no hasPro second-guessing here. The license's stored
  // `active` flag can lag the grace observation (a window opened the day before expiry), and the
  // backend already terminates the window when pro is restored.
  return school.restrictionRemovalDate ? moment(school.restrictionRemovalDate) : null;
}

/**
 * A school is in the removal grace window when it lost its pro upgrade, still restricts this
 * content, and the scheduled removal hasn't passed. Restrictions keep being enforced through the
 * window (Part 9), so the row warns rather than pretending the lock is already gone.
 */
export function isInRestrictionGraceWindow(
  school: AdminSchool,
  isRestricted: boolean,
  now: moment.Moment = moment()
): boolean {
  if (!isRestricted) {
    return false;
  }
  const removalDate = getRestrictionRemovalDate(school);
  return removalDate !== null && removalDate.isAfter(now);
}

/** "Aug 12th, 2026" — the removal-date wording the modal's warning lines use. */
export function formatRemovalDate(removalDate: moment.Moment): string {
  return removalDate.format('MMM Do, YYYY');
}
